import React from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

interface DoubleLineChartProps {
    data?: Array<{
        month: number;
        companyScore?: number;
        venueScore?: number;
    }>;
}

const DoubleLineChart = ({ data = [] }: DoubleLineChartProps) => {
    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const chartData = data.map((item) => ({
        month: monthNames[item.month - 1],
        companyScore: item.companyScore ?? 0,
        venueScore: item.venueScore ?? 0,
    }));

    const minValue = Math.min(
        ...data.map((item) =>
            Math.min(
                item.companyScore ?? 0,
                item.venueScore ?? 0
            )
        ),
        0
    );

    const yAxisMin = minValue > 0 ? minValue - 1 : minValue;

    /**
     * Custom shared tooltip
     */
    const CustomTooltip = ({
        active,
        payload,
        label,
    }: {
        active?: boolean;
        payload?: any[];
        label?: string;
    }) => {
        if (!active || !payload?.length) {
            return null;
        }

        const company = payload.find(
            (item) => item.dataKey === "companyScore"
        );

        const venue = payload.find(
            (item) => item.dataKey === "venueScore"
        );

        return (
            <div
                style={{
                    background: "#fff",
                    padding: "10px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,.15)",
                    minWidth: "150px",
                    fontFamily: "Inter, sans-serif",
                }}
            >
                {label && (
                    <div
                        style={{
                            fontWeight: 600,
                            marginBottom: "6px",
                            color: "#333",
                        }}
                    >
                        {label}
                    </div>
                )}

                {company && (
                    <div
                        style={{
                            color: "var(--secondary-color)",
                            fontWeight: 600,
                            marginBottom: "3px",
                        }}
                    >
                        Company Avg: {Number(company.value).toFixed(1)}%
                    </div>
                )}

                {venue && (
                    <div
                        style={{
                            color: "var(--primary-color)",
                            fontWeight: 600,
                        }}
                    >
                        Your Score: {Number(venue.value).toFixed(1)}%
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            style={{
                width: "100%",
                height: 300,
            }}
        >
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{
                        top: 10,
                        right: 10,
                        left: 0,
                        bottom: 0,
                    }}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke="#EAEAEA"
                    />

                    <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                            fontSize: 12,
                            fill: "#555",
                        }}
                    />

                    <YAxis
                        domain={[yAxisMin, 100]}
                        tickCount={6}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        tickFormatter={(value) =>
                            Math.ceil(value).toString()
                        }
                        tick={{
                            fontSize: 12,
                            fill: "#555",
                        }}
                    />

                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{
                            stroke: "#EAEAEA",
                            strokeDasharray: "4 4",
                        }}
                    />

                    {/* Company Average */}
                    <Line
                        type="linear"
                        dataKey="companyScore"
                        name="Company Avg"
                        stroke="var(--secondary-color)"
                        strokeWidth={2}
                        dot={{
                            r: 5,
                            strokeWidth: 0,
                            fill: "var(--secondary-color)",
                        }}
                        activeDot={{
                            r: 7,
                            strokeWidth: 0,
                            fill: "var(--secondary-color)",
                        }}
                        isAnimationActive={false}
                    />

                    {/* Your Score */}
                    <Line
                        type="linear"
                        dataKey="venueScore"
                        name="Your Score"
                        stroke="var(--primary-color)"
                        strokeWidth={2}
                        dot={{
                            r: 5,
                            strokeWidth: 0,
                            fill: "var(--primary-color)",
                        }}
                        activeDot={{
                            r: 7,
                            strokeWidth: 0,
                            fill: "var(--primary-color)",
                        }}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default DoubleLineChart;